import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useState, useRef, forwardRef } from 'react';
import classNames from 'classnames';
import head from 'lodash/fp/head';
import isArray from 'lodash/fp/isArray';
import noop from 'lodash/fp/noop';
import invariant from 'tiny-invariant';
import TreeCategory from './TreeCategory';
import Resizer from '../resizer/Resizer';
import TreeSidebar from './TreeSidebar';
import getWidthInBoundaries from '../../utils/getWidthInBoundaries';
import mergeRefs from '../../utils/mergeRefs';
import usePrevious from '../../usePrevious';
import SmoothScrollbars from '../smoothScrollbars/SmoothScrollbars';
const DEFAULT_WIDTH = 350;
const DEFAULT_MIN_WIDTH = 100;
const DEFAULT_MAX_WIDTH = 0;
const TreeMode = {
    MODE_FLY: 'fly',
    MODE_FLUID: 'fluid',
};
const getSidebarBodyRef = (sidebarRef) => { var _a; return head((_a = sidebarRef === null || sidebarRef === void 0 ? void 0 : sidebarRef.current) === null || _a === void 0 ? void 0 : _a.getElementsByClassName('AssetTreeBody')); };
const getCurrentCategoryElement = (children, currentCategoryId) => {
    return isArray(children) ? children.find(child => child && child.props.id === currentCategoryId) : children;
};
const renderTreesOffscreen = (children, categoryId) => {
    return React.Children.map(children, child => {
        const offscreenClasses = classNames('TreeOffscreenWrapper', 'height-100pct', child && child.props.id !== categoryId && 'position-offscreen');
        return _jsx("div", Object.assign({ className: offscreenClasses }, { children: child }));
    });
};
// Check if the child is a React element and if it has the type TreeCategory
const isTreeCategory = (child) => {
    return React.isValidElement(child) && child.type === TreeCategory;
};
const AssetTree = memo(forwardRef((props, ref) => {
    const { className, resizable = true, width = DEFAULT_WIDTH, minWidth = DEFAULT_MIN_WIDTH, maxWidth = DEFAULT_MAX_WIDTH, height, bordered = false, currentCategoryId, isOpen = true, useOffscreen = false, fly = false, onCategoryChange = noop, onToggleTree = noop, onResizeEnd = noop, children = [] } = props, remainingProps = __rest(props, ["className", "resizable", "width", "minWidth", "maxWidth", "height", "bordered", "currentCategoryId", "isOpen", "useOffscreen", "fly", "onCategoryChange", "onToggleTree", "onResizeEnd", "children"]);
    const getSidebarMode = (isFly) => (isFly ? TreeMode.MODE_FLY : TreeMode.MODE_FLUID);
    const [treeWidth, setTreeWidth] = useState(width);
    const [isResize, setIsResize] = useState(false);
    const [sidebarMode, setSidebarMode] = useState(getSidebarMode(fly));
    const sidebarRef = useRef(null);
    const mergedRefs = mergeRefs([ref, sidebarRef]);
    // Update internal state when props change
    const previousWidth = usePrevious(width);
    if (previousWidth !== width) {
        setTreeWidth(width);
    }
    const previousMode = usePrevious(fly);
    if (previousMode !== fly) {
        setSidebarMode(getSidebarMode(fly));
    }
    const childrenArray = React.Children.toArray(children);
    // check for children type of TreeCategory and throw error in case
    invariant(childrenArray.every(isTreeCategory), 'AssetTree only excepts children of type "TreeCategory"');
    const classes = classNames('AssetTree', className, !isOpen && 'closed', bordered && 'panel panel-default', sidebarMode === TreeMode.MODE_FLY ? 'fly' : 'fluid');
    const resizeLimitClasses = classNames('AssetTreeResizeLimit', isResize && 'display-block');
    const resizeIndicatorPosition = maxWidth || window.innerWidth * 0.5;
    const resizeLimitStyle = { left: resizeIndicatorPosition };
    const firstChild = head(childrenArray);
    const category = currentCategoryId ? getCurrentCategoryElement(childrenArray, currentCategoryId) : firstChild;
    const style = {
        width: treeWidth,
        height,
    };
    const handleToggleTreeContent = () => onToggleTree(!isOpen);
    const handleSelectCategory = (selectedCategoryId) => {
        onCategoryChange(selectedCategoryId);
        if (!isOpen) {
            handleToggleTreeContent();
        }
        else if (isOpen && currentCategoryId === selectedCategoryId) {
            handleToggleTreeContent();
        }
    };
    const handleResize = (diff) => {
        const halfWindowWidth = window.innerWidth * 0.5;
        const usedMaxWidth = maxWidth || halfWindowWidth;
        // Check for sidebar width if it is half window size. If it was before but the sidebar was resized so it is
        // no longer half window size, set the sidebar width to half the window size to avoid jumping sidebar
        // to old width
        setTreeWidth(oldWidth => {
            const updatedWidth = oldWidth - diff;
            return getWidthInBoundaries(minWidth, usedMaxWidth, updatedWidth);
        });
    };
    const handleResizeStart = () => {
        const body = getSidebarBodyRef(sidebarRef);
        if (body) {
            body.classList.add('pointer-events-none');
        }
        setIsResize(true);
    };
    const handleResizeEnd = () => {
        const body = getSidebarBodyRef(sidebarRef);
        if (body) {
            body.classList.remove('pointer-events-none');
        }
        setIsResize(false);
        onResizeEnd();
    };
    return (_jsxs("div", Object.assign({}, remainingProps, { className: classes, style: style, ref: mergedRefs }, { children: [_jsx("div", { className: resizeLimitClasses, style: resizeLimitStyle }), _jsxs("div", Object.assign({ className: 'AssetTreeContent' }, { children: [_jsx(TreeSidebar, Object.assign({ onSelectCategory: handleSelectCategory, currentCategoryId: currentCategoryId, onClick: handleToggleTreeContent }, { children: isArray(children) ? children : [children] })), _jsx(SmoothScrollbars, Object.assign({ slideIn: true, className: 'AssetTreeBody' }, { children: useOffscreen ? renderTreesOffscreen(childrenArray, currentCategoryId) : category }))] })), resizable && isOpen && (_jsx(Resizer, { onResizeStart: handleResizeStart, onResize: handleResize, onResizeEnd: handleResizeEnd, direction: Resizer.HORIZONTAL, position: Resizer.RIGHT }))] })));
}));
AssetTree.displayName = 'AssetTree';
Object.assign(AssetTree, TreeMode);
export default AssetTree;
